import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { SkeletonLoader, colors2 } from "oolib";
import {
  UI_BODY_SM,
  UI_TAG,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_BODY_SEMIBOLD_DF,
} from "oolib/dist/v2/components/Typo2";
import { DashboardWidgetShell } from "../../comps/DashboardWidgetShell";
import { PillDropdown } from "../../comps/PillDropdown";
import { StatusPill } from "../../comps/StatusPill";
import { useDetailPanel } from "../../comps/ActivePanelContext";
import { useGetSegmentLens } from "../../hooks/useGetLensData";
import { ConfidenceIndicator } from "../comps/ConfidenceIndicator";
import { TransparencyPanel } from "../comps/TransparencyPanel";
import { TagDetailSidebar } from "../comps/TagDetailSidebar";
import { WIDGET_STYLE, WIDGET_BODY_STYLE, SIGNAL_COLORS, getStatusTheme } from "../constants";
import { Users } from "@phosphor-icons/react";

// ── List styles ───────────────────────────────────────────────────

const SegmentList = styled.div`
  display: flex;
  flex-direction: column;
`;

const SegmentRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 1.2rem 0;
  border-bottom: 1px solid ${colors2.grey5};
  cursor: pointer;
  &:last-child { border-bottom: none; }
`;

const SegmentRowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

const SegmentDescription = styled(UI_BODY_SM)`
  color: ${colors2.grey60};
  line-height: 1.5;
`;

const DistRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const DistLabel = styled(UI_TAG)`
  color: ${colors2.grey40};
  text-transform: uppercase;
  flex-shrink: 0;
  margin-right: 2px;
`;

const Chip = styled(UI_TAG)`
  background: ${colors2.grey5};
  color: ${colors2.grey60};
  padding: 1px 6px;
  border-radius: 4px;
`;

function SentimentDist({ breakdown }) {
  const { positive = 0, negative = 0, neutral = 0 } = breakdown || {};
  if (positive + negative + neutral === 0) return null;
  return (
    <DistRow>
      <DistLabel>Sentiment</DistLabel>
      {positive > 0 && <UI_TAG style={{ color: SIGNAL_COLORS.positive }}>Positive {positive}</UI_TAG>}
      {negative > 0 && <UI_TAG style={{ color: SIGNAL_COLORS.negative }}>Negative {negative}</UI_TAG>}
      {neutral  > 0 && <UI_TAG style={{ color: colors2.grey50 }}>Neutral {neutral}</UI_TAG>}
    </DistRow>
  );
}

function TagDist({ label, items }) {
  if (!items?.length) return null;
  return (
    <DistRow>
      <DistLabel>{label}</DistLabel>
      {items.slice(0, 4).map((item) => (
        <Chip key={item.tag}>{item.tag} {item.count}</Chip>
      ))}
    </DistRow>
  );
}

// ── Attribute switcher options ────────────────────────────────────

const ATTRIBUTE_OPTIONS = [
  { value: "sentiments", display: "Sentiments" },
  { value: "signals",    display: "Signals" },
  { value: "emotions",   display: "Emotions" },
];

// ── Main block ────────────────────────────────────────────────────

export function SegmentLensBlock({ period, meta, showTransparency }) {
  const [dimension, setDimension] = useState(null);
  const [attribute, setAttribute] = useState(ATTRIBUTE_OPTIONS[0]);
  const { selected: selectedSegment, select: selectSegment, deselect: deselectSegment, isOpen: segmentPanelOpen } = useDetailPanel("lenses:segment");

  useEffect(() => { deselectSegment(); }, [period]);

  const metaDimensions = (meta?.segmentDimensions || []).map((d) => ({
    value: d.key,
    display: d.label,
  }));

  useEffect(() => {
    if (!dimension && metaDimensions.length > 0) setDimension(metaDimensions[0]);
  }, [metaDimensions.length]);

  const effectiveDimension = dimension || metaDimensions[0] || { value: "locality", display: "Locality" };

  const { data, isLoading } = useGetSegmentLens({
    dimension: effectiveDimension.value,
    period,
    attribute: attribute.value,
  });

  const selectedTheme = getStatusTheme("sentiment", selectedSegment?.sentiment);

  return (
    <>
    <DashboardWidgetShell
      titleJSX={
        <UI_BODY_SEMIBOLD_SM_DF style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <Users size={16} color={colors2.grey80} weight="bold" />
          <span>User segments</span>
          {metaDimensions.length > 0 && (
            <PillDropdown value={effectiveDimension} onChange={(id, val) => setDimension(val)} options={metaDimensions} />
          )}
          <span>against</span>
          <PillDropdown value={attribute} onChange={(id, val) => setAttribute(val)} options={ATTRIBUTE_OPTIONS} />
        </UI_BODY_SEMIBOLD_SM_DF>
      }
      style={WIDGET_STYLE}
      bodyStyle={WIDGET_BODY_STYLE}
    >
      {isLoading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={{ padding: "8px 0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <SkeletonLoader style={{ height: 14, width: `${30 + Math.random() * 20}%`, borderRadius: 4 }} />
                <SkeletonLoader style={{ height: 22, width: 65, borderRadius: 12 }} />
              </div>
              <SkeletonLoader style={{ height: 10, width: "70%", borderRadius: 4, marginTop: 6 }} />
              <SkeletonLoader style={{ height: 10, width: "50%", borderRadius: 4, marginTop: 4 }} />
            </div>
          ))}
        </div>
      )}
      {!isLoading && !data?.segments?.length && (
        <UI_BODY_SM style={{ color: colors2.grey40 }}>No segment data available. Annotations need customer links to power this view.</UI_BODY_SM>
      )}
      {!isLoading && (
        <SegmentList>
          {(data?.segments || []).map((seg) => {
            const sentimentTheme = getStatusTheme("sentiment", seg.sentiment);
            const topSignal   = seg.topSignals?.[0];
            const topEmotion  = seg.emotionBreakdown?.[0];
            const neutralPillTheme = { bg: colors2.grey10, color: colors2.grey70 };

            const dominantPill =
              attribute.value === "sentiments" ? <StatusPill theme={sentimentTheme} /> :
              attribute.value === "signals"    && topSignal  ? <StatusPill theme={neutralPillTheme} label={`${topSignal.tag} ${topSignal.count}`} /> :
              attribute.value === "emotions"   && topEmotion ? <StatusPill theme={neutralPillTheme} label={`${topEmotion.tag} ${topEmotion.count}`} /> :
              null;

            return (
              <SegmentRow key={seg.segment} onClick={() => selectSegment(seg)}>
                <SegmentRowTop>
                  <UI_BODY_SEMIBOLD_DF style={{ color: colors2.grey90 }}>{seg.segment}</UI_BODY_SEMIBOLD_DF>
                  {dominantPill}
                </SegmentRowTop>
                {seg.description && (
                  <SegmentDescription>{seg.description}</SegmentDescription>
                )}
                <div style={{ marginTop: 4 }}>
                  {attribute.value === "sentiments" && <SentimentDist breakdown={seg.sentimentBreakdown} />}
                  {attribute.value === "signals"    && <TagDist label="Signals"  items={seg.topSignals} />}
                  {attribute.value === "emotions"   && <TagDist label="Emotions" items={seg.emotionBreakdown} />}
                </div>
              </SegmentRow>
            );
          })}
          {showTransparency && data?.coverage && (
            <>
              <ConfidenceIndicator confidence={data.coverage.confidence} summary={data.coverage.summary} />
              <TransparencyPanel warnings={data.coverage.warnings} />
            </>
          )}
        </SegmentList>
      )}
    </DashboardWidgetShell>

    <TagDetailSidebar
      isOpen={segmentPanelOpen}
      onClose={deselectSegment}
      title={selectedSegment?.segment}
      overline={effectiveDimension?.display}
      mentionsCount={selectedSegment?.annotationCount}
      mentionsPeriod="this period"
      description={selectedSegment?.description}
      mentions={selectedSegment?.mentions}
      showSentimentPolarity
      sentimentBreakdown={selectedSegment?.sentimentBreakdown}
      primaryColor={selectedTheme?.accent}
      glowColor={selectedTheme?.glow}
      pill={selectedTheme && <StatusPill theme={selectedTheme} />}
    />
    </>
  );
}
